<template>
    <div class="relative">
        <label
            :for="id"
            class="absolute bg-base-100 top-[-11px] left-[12px] pl-1 pr-1 text-xs z-1"
        > {{ label }}
        </label>
        <div
            class="flex items-center h-full w-full"
            :class="{'border-error':!focused && touched && !!validationError, 'focus:border-base-300': focused}"
        >
            <textarea
                v-if="textarea"
                :id="id"
                ref="inputRef"
                class="border border-base-300 rounded-none focus:outline-none w-full bg-base-100"
                data-cy="input-field"
                :placeholder="placeholder"
                :value="modelValue as string"
                :disabled="disabled"
                autocomplete="off"
                @blur="onBlur"
                @keyup="onInput"
                @focusin="focused = true"
            />
            <input
                v-else
                :id="id"
                ref="inputRef"
                :type="type"
                class="input input-sm border border-base-300 rounded-none focus:outline-none w-full truncate"
                :class="{'pl-8': icon}"
                :data-cy="dataCy"
                :placeholder="placeholder"
                :value="modelValue"
                :disabled="disabled"
                autocomplete="off"
                @blur="onBlur"
                @keyup="onInput"
                @focusin="focused = true"
            >
            <i
                v-if="!!validationError"
                class="i-ph-warning-fill text-error absolute right-2 top-1/2 transform -translate-y-1/2"
                :title="$t(validationError)"
            />
            <i
                v-else
                class="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                :class="icon"
            />
        </div>
    </div>
</template>

<script setup lang="ts" generic="T extends string | number">
// Deprecated: Use specific input components from NuxtUI instead. If you need validation, put them in a forms
import {computed, PropType, ref} from 'vue'
import {HTMLInputElement} from 'happy-dom'
import {Maybe} from '@/types/Maybe'
import {v4 as uuidv4} from 'uuid'

const inputRef = ref<InstanceType<typeof HTMLInputElement> | null>(null)
const touched = ref(false)
const focused = ref(false)

const id = uuidv4()

const props = defineProps({
    modelValue: {type: [String, Number]},
    label: String,
    placeholder: String,
    type: String,
    textarea: {type: Boolean, default: false},
    required: {type: Boolean, default: false},
    customEventListener: Function,
    isValid: {
        type: Function as PropType<(arg0: Maybe<T>) => string | undefined>,
        required: false
    },
    icon: String,
    disabled: {type: Boolean, default: false},
    dataCy: {type: String, default: 'input-field'},
})

const emit = defineEmits(['update:modelValue', 'blur'])

const onBlur = (event: any) => {
    emit('update:modelValue', getValue(event))
    emit('blur')
    touched.value = true
    focused.value = false
}

const onInput = (event: any) => {
    if (props.customEventListener) {
        props.customEventListener(event)
    }
    emit('update:modelValue', getValue(event))
    touched.value = true
}

const getValue = (event: {target: HTMLInputElement}): T => {
    if (props.type === 'number') {
        return event.target.valueAsNumber as T
    }
    return event.target.value as T
}

const validationError = computed(() => {
    if (props.isValid) {
        return props.isValid(props.modelValue as T)
    }
    return undefined
})

const focus = () => {
    inputRef.value?.focus()
}

defineExpose({focus})

</script>

<style scoped>
input[type='number']::-webkit-inner-spin-button,
input[type='number'] {
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield !important;
}


</style>
